import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import { useHotels, getLocalizedText } from '../hooks/useHotels'
import { MapPin, Star, Filter, Search } from 'lucide-react'

export function Hotels() {
  const { t } = useTranslation()
  const { language, isRTL } = useLanguage()
  const [selectedCity, setSelectedCity] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const { hotels, loading, getUniqueCities } = useHotels()
  const uniqueCities = getUniqueCities()

  // Get city from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const cityParam = params.get('city')
    if (cityParam) {
      setSelectedCity(cityParam)
    }
  }, [])

  // Filter hotels by selected city and search query
  const filteredHotels = hotels.filter(hotel => {
    const matchesCity = !selectedCity || hotel.city.en === selectedCity
    const hotelName = getLocalizedText(hotel.name, language).toLowerCase()
    const hotelCity = getLocalizedText(hotel.city, language).toLowerCase()
    const matchesSearch = !searchQuery || 
      hotelName.includes(searchQuery.toLowerCase()) ||
      hotelCity.includes(searchQuery.toLowerCase())
    return matchesCity && matchesSearch
  })

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-foreground mb-4">
            {t('hotels.title', 'Hotels')}
          </h1>
          <p className="text-muted-foreground text-lg">
            {t('hotels.subtitle', 'Find the perfect accommodation for your journey')}
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 space-y-4"
        >
          {/* Search Bar */}
          <div className="relative">
            <Search className={`absolute top-1/2 transform -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
            <input
              type="text"
              placeholder={t('hotels.searchPlaceholder', 'Search hotels...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-12 pr-4 py-3 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${isRTL ? 'text-right' : 'text-left'}`}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>

          {/* City Filter */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-muted-foreground" />
              <span className="text-muted-foreground font-medium">
                {t('hotels.filterByCity', 'Filter by City')}:
              </span>
            </div>
            <button
              onClick={() => setSelectedCity('')}
              className={`px-4 py-2 rounded-full transition-colors ${
                !selectedCity
                  ? 'bg-primary text-white'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              {t('hotels.allCities', 'All Cities')}
            </button>
            {uniqueCities.map((city) => (
              <button
                key={city}
                onClick={() => setSelectedCity(city)}
                className={`px-4 py-2 rounded-full transition-colors ${
                  selectedCity === city
                    ? 'bg-primary text-white'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
              >
                {city}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">{t('hotels.loading', 'Loading hotels...')}</p>
          </div>
        )}

        {/* Hotels Grid */}
        {!loading && (
          <>
            {filteredHotels.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">
                  {t('hotels.noHotelsFound', 'No hotels found')}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredHotels.map((hotel, index) => (
                  <motion.div
                    key={hotel.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={`/hotel/${hotel.id}`}
                      className="block bg-card border border-border rounded-xl overflow-hidden hover:shadow-xl transition-shadow duration-300 group"
                    >
                      {/* Hotel Image */}
                      <div className="relative h-48 overflow-hidden">
                        {hotel.images && hotel.images.length > 0 ? (
                          <img
                            src={hotel.images[0]}
                            alt={getLocalizedText(hotel.name, language)}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full bg-secondary flex items-center justify-center">
                            <MapPin className="h-12 w-12 text-muted-foreground" />
                          </div>
                        )}
                        {hotel.rating && (
                          <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full flex items-center gap-1">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm font-medium">{hotel.rating.toFixed(1)}</span>
                          </div>
                        )}
                      </div>

                      {/* Hotel Info */}
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                          {getLocalizedText(hotel.name, language)}
                        </h3>
                        <div className="flex items-center gap-2 text-muted-foreground mb-4">
                          <MapPin className="h-4 w-4" />
                          <span className="text-sm">{getLocalizedText(hotel.city, language)}</span>
                        </div>
                        {hotel.description && getLocalizedText(hotel.description, language) && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {getLocalizedText(hotel.description, language)}
                          </p>
                        )}
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

